import { useContext, useState } from "react"
import { Chip } from "@heroui/react"
import { CircleStackIcon } from "@heroicons/react/24/outline"
import { useLazyQuery, useQuery } from "@apollo/client"

import {
  DataTable,
  EllipsisDropdown,
  EmptyPlaceholder,
  IntegrationTypeBadge,
  MisBadge,
  MisCountBadge,
  Page,
  PageHeader,
  SiteDataMenu,
  StudentGuardiansList,
  StudentLessonsList,
} from "@components"

import { ContextualSidebarContext, UserContext } from "@context"

import { 
  GET_STUDENTS, 
  GET_SITE_DATA_STUDENTS_SUMMARY, 
  GET_SITE_DATA_STUDENTS_XLS_DOWNLOAD_LINK 
} from "@graphql"

import { 
  downloadFile, 
  lastImportDuration, 
  mySqlToLocal,
  showLoader,
  studentFullname 
} from "@helpers"

import {   
  useColumnManager,
  useSetActiveSidebarLink 
} from "@hooks"

import { 
  columns, 
  defaultColumns, 
  downloadOptions, 
  filters 
} from "./data"

import { Stats } from "./Stats"

const Students = () => {
  const { user } = useContext(UserContext)
  const { setContextualSidebar } = useContext(ContextualSidebarContext)
  const [isDownloading, setIsDownloading] = useState(false)
  const [refetching, setRefetching] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState(Object.keys(filters))
  const [searchTerm, setSearchTerm] = useState("")
  const [sortDescriptor, setSortDescriptor] = useState({ column: "surname", direction: "ascending" })
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)

  useSetActiveSidebarLink("SITE_DATA_STUDENTS", "SITE_DATA")
  const [selectedColumns, setSelectedColumns] = useColumnManager(columns, "data.students.columns", defaultColumns)

  const summaryData = useQuery(GET_SITE_DATA_STUDENTS_SUMMARY, {
    variables: {
      clientId: user.me.client.id,
    },
    fetchPolicy: "cache-and-network", 
    pollInterval: 900000,
  })

  const { loading, error, data, refetch, networkStatus } = useQuery(GET_STUDENTS, {
    variables: {
      searchTerm,
      page,
      perPage,
      clientId: user.me.client.id,
      orderBy: [
        {
          column: sortDescriptor.column.toUpperCase(),
          order: sortDescriptor.direction === "ascending" ? "ASC" : "DESC",
        },
      ],
      where: selectedFilters.length < 2 ? { column: "IS_SYNC", value: selectedFilters.includes("sync") } : null,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 900000,
  })

  const [getSiteDataStudentsXlsDownloadLink] = useLazyQuery(GET_SITE_DATA_STUDENTS_XLS_DOWNLOAD_LINK, {
    variables: { clientId: user.me.client.id },
    fetchPolicy: "network-only",
    onCompleted: data => {
      downloadFile(data.dataStudentsXlsDownload.filename, data.dataStudentsXlsDownload.url)
      setIsDownloading(false)
    },
  })

  const handleRefetchButtonClicked = () => {
    setRefetching(true)
    refetch().then(() => setRefetching(false))
  }

  const handleDownload = type => {
    if (type === "xls") {
      setIsDownloading(true)
      getSiteDataStudentsXlsDownloadLink()
    }
  }

  const handleSearchClear = () => {
    setSearchTerm("")
    setPage(1)
  }

  const handleSearchChange = searchTerm => {
    setSearchTerm(searchTerm)
    setPage(1)
  }

  const handleShowGuardians = student =>
    setContextualSidebar(prevState => ({
      ...prevState,
      open: true,
      title: studentFullname(student),
      subTitle: `${studentFullname(student)} guardians are listed below.`,
      content: <StudentGuardiansList student={student} />,
      size: "lg",
    }))

  const handleShowLessons = student =>
    setContextualSidebar(prevState => ({
      ...prevState,
      open: true,
      title: studentFullname(student),
      subTitle: `${studentFullname(student)} lessons are listed below.`,
      content: <StudentLessonsList student={student} />,
      size: "lg",
    }))

  const handleSortChange = sortDescriptor => {
    setSortDescriptor(sortDescriptor)
    setPage(1)
  }

  const handleToggleColumn = selectedColumns => {
    setSelectedColumns(Object.keys(columns).filter(column => selectedColumns.has(column)))
  }

  const handleToggleFilter = selectedFilters => {
    setSelectedFilters(Object.keys(filters).filter(filter => selectedFilters.has(filter)))
  }

  const renderCell = (student, column) => {
    if (column === "actions") {
      const items = [
        { id: 1, handleClick: () => handleShowGuardians(student), caption: "View Guardians" },
        { id: 2, handleClick: () => handleShowLessons(student), caption: "View Lessons" },
      ]

      return <EllipsisDropdown items={items} />
    }

    if (["created_at", "updated_at"].includes(column)) {
      return mySqlToLocal(student[column]).format("YYYY-MM-DD HH:mm")
    }

    if (column === "is_sync" && student.is_sync) {
      return (
        <Chip classNames={{ base: "bg-sb-sync", content: "text-white" }} size="sm" variant="flat">
          Sync
        </Chip>
      )
    }

    if (column === "is_sync" && !student.is_sync) {
      return (
        <Chip classNames={{ base: "bg-sb-sync-custom", content: "text-white" }} size="sm" variant="flat">
          Custom
        </Chip>
      )
    }

    return student[column]
  }

  if (error) return `Error! ${error.message}`

  const showLoadingPlaceholder = showLoader(loading, networkStatus, data?.client?.students?.data, refetching);

  return (
    <Page>
      <PageHeader
        title="Student Data"
        subTitle="Imported student data from your MIS / CSV file."
        icon={<CircleStackIcon className="page-header-icon" />}
        contentBottom={
          <div className="flex flex-wrap gap-2">
            <IntegrationTypeBadge name={user?.me?.client?.importType?.name || "unknown"} />
            <MisBadge name={user?.me?.client?.mis || "n/a"} />
            <MisCountBadge name={`${summaryData?.data?.dataStudentsSummary?.total || 0} Students`} />
          </div>
        }
        actions={<SiteDataMenu clientId={user.me.client.id} />}
      />

      <Stats
        summary={{
          dataAge: summaryData?.data?.dataStudentsSummary?.data_age,
          lastImport: lastImportDuration(summaryData?.data?.dataStudentsSummary?.imported_at),
          total: summaryData?.data?.dataStudentsSummary?.total,
          syncCount: summaryData?.data?.dataStudentsSummary?.sync_count,
          customCount: summaryData?.data?.dataStudentsSummary?.custom_count,
          missingLessonCount: summaryData?.data?.dataStudentsSummary?.missing_lesson_count,
          missingGuardianCount: summaryData?.data?.dataStudentsSummary?.missing_guardian_count,
        }}
      />

      <DataTable
        columns={columns}
        currentPage={page}
        currentSortOrder={sortDescriptor}
        data={data?.client?.students?.data}
        downloadOptions={downloadOptions}
        filters={filters}
        handleDownload={type => handleDownload(type)}
        handlePageChange={page => setPage(page)}
        handleRefetch={() => handleRefetchButtonClicked()}
        handleRenderCell={(item, key) => renderCell(item, key)}
        handleSearchChange={searchTerm => handleSearchChange(searchTerm)}
        handleSearchClear={handleSearchClear}
        handleSortChange={sortDescriptor => handleSortChange(sortDescriptor)}
        handleToggleColumn={selectedColumns => handleToggleColumn(selectedColumns)}
        handleToggleFilter={selectedFilters => handleToggleFilter(selectedFilters)}
        isDownloading={isDownloading}
        loading={showLoadingPlaceholder}
        isRefetching={refetching}
        noDataMessage={<EmptyPlaceholder classNames="mt-2" title="No Students Found" description="No Students found! Try clearing any filters." />}
        paginatorInfo={data?.client?.students?.paginatorInfo}
        searchInputPlaceHolder="Search by forename / surname"
        searchTerm={searchTerm}
        selectedColumns={selectedColumns}
        selectedFilters={selectedFilters}
        showDownloadButton={true}
        showRefetchButton={true}
      />
    </Page>
  )
}

export default Students
